import { Alert, Badge, Group, SegmentedControl, Stack, Table, Text, Title } from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminShell } from '../../../components/admin-shell/AdminShell';
import { AdminTableSection } from '../../../components/admin-shell/AdminTableSection';
import { usePaginatedQuery } from '../_hooks';

type EvidenceKind =
  | 'id_document'
  | 'selfie'
  | 'business_registration'
  | 'tax_id'
  | 'utility_bill';

type VerificationState = 'pending' | 'approved' | 'rejected' | 'revoked';

// API subject_kind enum. 'all' is a UI-only sentinel — it omits the param.
type SubjectKind = 'artist' | 'host_user' | 'venue';
type SubjectKindFilter = 'all' | SubjectKind;

const SUBJECT_KIND_LABEL: Record<SubjectKind, string> = {
  artist: 'Artist',
  host_user: 'Host',
  venue: 'Venue',
};

interface VerificationRow {
  id: string;
  subject_kind: 'artist' | 'host_user' | 'venue';
  subject_id: string;
  subject_label: string;
  state: VerificationState;
  submitted_at: string;
  sla: 'ok' | 'at_risk' | 'breached';
  expires_at: string | null;
  rejection_reason: string | null;
  evidence_kinds: EvidenceKind[];
}

const EVIDENCE_LABEL: Record<EvidenceKind, string> = {
  id_document: 'ID',
  selfie: 'Selfie',
  business_registration: 'Business reg.',
  tax_id: 'Tax ID',
  utility_bill: 'Utility',
};

export const Route = createFileRoute('/admin/verifications/')({
  component: AdminVerificationsList,
});

function AdminVerificationsList() {
  const [state, setState] = useState<VerificationState>('pending');
  const [subjectKind, setSubjectKind] = useState<SubjectKindFilter>('all');

  const { data, isLoading, error, page, setPage, totalPages, bindFilter } =
    usePaginatedQuery<VerificationRow>({
      queryKey: ['admin', 'verifications'],
      endpoint: '/api/admin/verifications',
      // 'all' omits subject_kind (the hook drops undefined filter values).
      filters: { state, subject_kind: subjectKind === 'all' ? undefined : subjectKind },
    });

  // T3 — overdue (pending past the 7-day review SLA) surfaces above the queue
  // so SLA-slip is visible regardless of the current state filter. Server
  // computes against the breach threshold; see
  // worker/routes/admin/verifications.ts loadOverdueVerificationCount.
  const overdueCount = data?.overdue_count ?? 0;

  return (
    <AdminShell>
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" align="center">
          <Title order={2}>Verifications</Title>
          {overdueCount > 0 ? (
            <Badge color="red" variant="filled" size="lg" radius="sm">
              Overdue: {overdueCount}
            </Badge>
          ) : null}
        </Group>

        <Group gap="md" wrap="wrap" align="flex-end">
          <SegmentedControl
            value={subjectKind}
            onChange={bindFilter((v: string) => setSubjectKind(v as SubjectKindFilter))}
            data={[
              { label: 'All', value: 'all' },
              { label: 'Venues', value: 'venue' },
              { label: 'Artists', value: 'artist' },
              { label: 'Hosts', value: 'host_user' },
            ]}
          />
          <SegmentedControl
            value={state}
            onChange={bindFilter((v: string) => setState(v as VerificationState))}
            data={[
              { label: 'Pending', value: 'pending' },
              // Display label only — the underlying state value stays 'approved'.
              { label: 'Verified', value: 'approved' },
              { label: 'Rejected', value: 'rejected' },
              { label: 'Revoked', value: 'revoked' },
            ]}
          />
        </Group>

        {subjectKind === 'artist' ? (
          <Alert color="tide" variant="light" radius="md">
            Artists are verified automatically once they confirm a mobile number, set a profile
            photo, and add a link. This queue only carries artists an admin has flagged for a manual
            look — most of the time it stays empty.
          </Alert>
        ) : null}

        <AdminTableSection<VerificationRow>
          data={data}
          isLoading={isLoading}
          error={error}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          emptyMessage="Nothing in the queue."
          header={
            <Table.Tr>
              <Table.Th>Subject</Table.Th>
              <Table.Th>Kind</Table.Th>
              <Table.Th>Evidence</Table.Th>
              <Table.Th>Submitted</Table.Th>
              <Table.Th>SLA</Table.Th>
              <Table.Th>State</Table.Th>
            </Table.Tr>
          }
          renderRow={(v) => (
            <Table.Tr key={v.id}>
              <Table.Td>
                <Link to="/admin/verifications/$id" params={{ id: v.id }}>
                  {v.subject_label}
                </Link>
              </Table.Td>
              <Table.Td>{SUBJECT_KIND_LABEL[v.subject_kind] ?? v.subject_kind}</Table.Td>
              <Table.Td>
                {v.evidence_kinds.length === 0 ? (
                  <Text size="xs" c="dimmed">
                    –
                  </Text>
                ) : (
                  <Group gap={4} wrap="wrap">
                    {v.evidence_kinds.map((k) => (
                      <Badge key={k} size="xs" variant="light" color="gray">
                        {EVIDENCE_LABEL[k] ?? k}
                      </Badge>
                    ))}
                  </Group>
                )}
              </Table.Td>
              <Table.Td>{relative(v.submitted_at)}</Table.Td>
              <Table.Td>
                <Badge color={slaColor(v.sla)} variant="light">
                  {v.sla}
                </Badge>
              </Table.Td>
              <Table.Td>{stateLabel(v.state)}</Table.Td>
            </Table.Tr>
          )}
        />
      </Stack>
    </AdminShell>
  );
}

// Spec §5 badge language: the 'approved' state reads as "Verified" everywhere
// in the admin UI. Underlying state value is unchanged.
function stateLabel(state: VerificationState): string {
  if (state === 'approved') return 'Verified';
  if (state === 'pending') return 'Pending';
  if (state === 'rejected') return 'Rejected';
  return 'Revoked';
}

function slaColor(sla: string): string {
  if (sla === 'breached') return 'red';
  if (sla === 'at_risk') return 'yellow';
  return 'tide';
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
